import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import './index.css';
import './utils/clearBadStorageData.ts';
import { initStorageCleanup } from './utils/clearCorruptedStorage.ts';
import { SENTRY_CONFIG, TELEMETRY_DISABLED } from './lib/config';
import { beforeSend as alertingBeforeSend } from './lib/alerting';
import { runWhenIdle } from './lib/deferredSdk';
import { loadMixpanel } from './lib/mixpanel';
import { loadPostHog } from './lib/posthog';
import { initWebVitals } from './lib/webVitals';
import { initMetrics } from './lib/metrics';
import { runStartupHealthCheck } from './lib/healthCheck';
import { initializeDarkMode } from './store/themeStore';
import logger from './lib/logger';

function initCloudflareBeacon() {
  if (import.meta.env.DEV || TELEMETRY_DISABLED) {
    return;
  }

  const script = document.createElement('script');
  script.defer = true;
  script.src = 'https://static.cloudflareinsights.com/beacon.min.js';
  script.setAttribute('data-cf-beacon', '{"token": "adfcfd9a169c45ae87cc0abfabf9f6ee"}'); // pragma: allowlist secret
  document.body.appendChild(script);
}

async function loadSentryReplay() {
  if (!SENTRY_CONFIG.enabled) {
    return;
  }

  try {
    // Imported from the replay package directly: @sentry/react is in the eager
    // chunk, so a dynamic import of it would not split replay out.
    const { replayIntegration } = await import('@sentry-internal/replay');
    Sentry.addIntegration(replayIntegration());
  } catch (error) {
    logger.warn('Sentry: failed to load session replay', error);
  }
}

// Initialize Sentry FIRST before anything else
if (SENTRY_CONFIG.enabled) {
  Sentry.init({
    dsn: SENTRY_CONFIG.dsn,
    environment: SENTRY_CONFIG.environment,
    release: SENTRY_CONFIG.release,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration({
        enableLongTask: true,
        enableLongAnimationFrame: true,
        enableInp: true,
        tracePropagationTargets: SENTRY_CONFIG.tracePropagationTargets,
      }),
    ],
    tracesSampleRate: SENTRY_CONFIG.tracesSampleRate,
    profilesSampleRate: SENTRY_CONFIG.profilesSampleRate,
    replaysSessionSampleRate: SENTRY_CONFIG.replaysSessionSampleRate,
    replaysOnErrorSampleRate: SENTRY_CONFIG.replaysOnErrorSampleRate,
    beforeSend: alertingBeforeSend,
    initialScope: {
      tags: {
        service: 'insider-performance-frontend',
      },
    },
  });
  logger.log(`Sentry initialized for ${SENTRY_CONFIG.environment} environment`);
}

if (!TELEMETRY_DISABLED) {
  initCloudflareBeacon();
  initWebVitals();
  initMetrics();
  runStartupHealthCheck();
}

// Initialize storage cleanup before app starts
initStorageCleanup();

// Initialize theme (system preference detection and DOM class)
initializeDarkMode();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);

// Analytics and session replay live in their own chunks. Events fired before
// they load are queued by withMixpanel/withPostHog.
if (!TELEMETRY_DISABLED) {
  runWhenIdle(() => {
    void loadMixpanel();
    void loadPostHog();
    void loadSentryReplay();
  });
}
